import React from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Typography,
  Tooltip,
  Icon,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import styles from './styles';

const useStyles = makeStyles(styles);

export const ConfigurationsFormComponent = (props) => {
  const {
    eventTemplate,
    handleChangeAllowActiveDuplicates,
    handleChangeConfigurableTags,
    handleChangeConfigurableName,
    handleChangeConfigurableStartStatus,
    isLoading,
  } = props;
  const classes = useStyles();

  const label = (
    <Grid item xs={12} md={3} lg={2}>
      <Typography variant="subtitle2">Configurations</Typography>
      <Typography variant="caption" component="div" gutterBottom>
        Set whether you want to allow more than one active event for this workflow.
      </Typography>
      <Typography variant="caption" component="div" gutterBottom>
        Configure whether the name, starting status, or tags should be configurable when using this workflow.
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid container spacing={4}>
        {label}
        <Grid item xs={12} md={9} lg={10} container className={classes.section}>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      {label}
      <Grid item xs={12} md={9} lg={10} container className={classes.section}>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="subtitle1">Creation Configurations</Typography>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={eventTemplate.allowActiveDuplicates}
                    onChange={handleChangeAllowActiveDuplicates}
                    value="allowActiveDuplicates"
                    color="primary"
                  />
                )}
                label={(
                  <div className={classes.toggleFieldLabelContainer}>
                    <Typography
                      className={classes.toggleFieldLabel}
                    >
                      Duplicate Active Events
                    </Typography>
                    <Tooltip title="If an event created from this workflow is active (not complete or archived), can another event be created from this workflow resulting in more than one active at a time?">
                      <Icon fontSize="small" color="disabled">info_outline</Icon>
                    </Tooltip>
                  </div>
                )}
              />

              <FormControlLabel
                control={(
                  <Checkbox
                    checked={eventTemplate.configurableName}
                    onChange={handleChangeConfigurableName}
                    value="configurableName"
                    color="primary"
                  />
                )}
                label={(
                  <div className={classes.toggleFieldLabelContainer}>
                    <Typography
                      className={classes.toggleFieldLabel}
                    >
                      Configurable Name
                    </Typography>
                    <Tooltip title="When a user selects this workflow at event creation, can the name of the event be changed from the name defined in the workflow? If off, the name is locked and cannot be changed.">
                      <Icon fontSize="small" color="disabled">info_outline</Icon>
                    </Tooltip>
                  </div>
                )}
              />

              <FormControlLabel
                control={(
                  <Checkbox
                    checked={eventTemplate.configurableStartStatus}
                    onChange={handleChangeConfigurableStartStatus}
                    value="configurableStartStatus"
                    color="primary"
                  />
                )}
                label={(
                  <div className={classes.toggleFieldLabelContainer}>
                    <Typography className={classes.toggleFieldLabel}>
                      Configurable Starting Status
                    </Typography>
                    <Tooltip title="When a user selects this workflow at event creation, can the starting status of the event be changed from the status defined in the workflow? If off, the status selection is locked and cannot be changed.">
                      <Icon fontSize="small" color="disabled">info_outline</Icon>
                    </Tooltip>
                  </div>
                )}
              />

              <FormControlLabel
                control={(
                  <Checkbox
                    checked={eventTemplate.configurableTags}
                    onChange={handleChangeConfigurableTags}
                    value="configurableTags"
                    color="primary"
                  />
                )}
                label={(
                  <div className={classes.toggleFieldLabelContainer}>
                    <Typography
                      className={classes.toggleFieldLabel}
                    >
                      Configurable Tags
                    </Typography>
                    <Tooltip title="When a user selects this workflow at event creation, can the tags defined in the workflow be changed (add or remove tags)? If off, tags are locked and cannot be changed.">
                      <Icon fontSize="small" color="disabled">info_outline</Icon>
                    </Tooltip>
                  </div>
                )}
              />
            </FormGroup>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

ConfigurationsFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  handleChangeAllowActiveDuplicates: PropTypes.func,
  handleChangeConfigurableTags: PropTypes.func,
  handleChangeConfigurableName: PropTypes.func,
  handleChangeConfigurableStartStatus: PropTypes.func,
  isLoading: PropTypes.bool,
};

ConfigurationsFormComponent.defaultProps = {
  eventTemplate: {},
  handleChangeAllowActiveDuplicates: () => {},
  handleChangeConfigurableTags: () => {},
  handleChangeConfigurableName: () => {},
  handleChangeConfigurableStartStatus: () => {},
  isLoading: true,
};

export default ConfigurationsFormComponent;
